import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";

/**
 * Command palette (Ctrl+K / Cmd+K) untuk cari fitur/menu.
 * Props:
 *   - sections: array of { section: str, items: [{ to, label, icon, tid, mod? }] }
 *   - open, onClose
 */
export default function CommandPalette({ open, onClose, sections = [] }) {
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const nav = useNavigate();

  const flatItems = useMemo(() => {
    const items = [];
    sections.forEach(sec => (sec.items || []).forEach(it => items.push({ ...it, section: sec.section })));
    return items;
  }, [sections]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return flatItems;
    return flatItems.filter(it =>
      (it.label || "").toLowerCase().includes(s) ||
      (it.section || "").toLowerCase().includes(s) ||
      (it.to || "").toLowerCase().includes(s)
    );
  }, [q, flatItems]);

  useEffect(() => {
    if (open) { setQ(""); setActiveIdx(0); setTimeout(() => inputRef.current?.focus(), 60); }
  }, [open]);

  useEffect(() => { setActiveIdx(0); }, [q]);

  const goTo = (item) => {
    if (!item) return;
    nav(item.to);
    onClose();
  };

  const onKey = (e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); goTo(filtered[activeIdx]); }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx='${activeIdx}']`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  if (!open) return null;

  // Group filtered items by section for display
  const grouped = {};
  filtered.forEach((it, i) => {
    if (!grouped[it.section]) grouped[it.section] = [];
    grouped[it.section].push({ ...it, _idx: i });
  });

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm p-4 flex items-start justify-center pt-[10vh]"
      onClick={onClose} data-testid="command-palette-overlay">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="command-palette">
        <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Cari fitur atau menu... (misalnya 'sales', 'kpi', 'absensi')"
            data-testid="command-palette-input"
            className="flex-1 h-9 outline-none text-sm bg-transparent"
          />
          <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 rounded border border-slate-200 text-[10px] font-mono text-slate-500">ESC</kbd>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div ref={listRef} className="max-h-[55vh] overflow-y-auto py-2">
          {Object.entries(grouped).length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-slate-500">
              Tidak ada fitur yang cocok dengan pencarian &quot;<b>{q}</b>&quot;.
            </div>
          )}
          {Object.entries(grouped).map(([sec, items]) => (
            <div key={sec} className="mb-1">
              <p className="px-4 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{sec}</p>
              {items.map(it => {
                const Icon = it.icon;
                const active = it._idx === activeIdx;
                return (
                  <button
                    key={it.to}
                    data-idx={it._idx}
                    data-testid={`cmd-${(it.to || "").replace(/\//g, "-")}`}
                    onMouseEnter={() => setActiveIdx(it._idx)}
                    onClick={() => goTo(it)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      active ? "bg-emerald-50 text-emerald-800" : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {Icon && <Icon className={`w-4 h-4 shrink-0 ${active ? "text-emerald-600" : "text-slate-400"}`} />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{it.label}</p>
                      <p className="text-[10px] text-slate-400 truncate font-mono">{it.to}</p>
                    </div>
                    {active && <ArrowRight className="w-4 h-4 text-emerald-500 shrink-0" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 px-4 py-2 bg-slate-50 flex items-center justify-between text-[10px] text-slate-400">
          <div className="flex gap-3">
            <span><kbd className="px-1 py-0.5 rounded bg-white border border-slate-200 font-mono">↑↓</kbd> pilih</span>
            <span><kbd className="px-1 py-0.5 rounded bg-white border border-slate-200 font-mono">Enter</kbd> buka</span>
          </div>
          <span>{filtered.length} hasil</span>
        </div>
      </div>
    </div>
  );
}
